/**
 * Decode the (unverified) JSON header of a share-session token — the client only
 * reads it for display / routing / identity hints (rootId, fileId, verifiedEmail,
 * at); the server re-verifies the signature on every call. Shared by the viewer
 * chrome (which session the body renders against) and the explorer (whether a
 * token change is a plain file switch or a genuine identity switch).
 */
export function decodeSessionPayload(
  sid: string | undefined | null,
): { tenantId?: string; verifiedEmail?: string; rootId?: string; at?: string; ae?: string; fileId?: string; xt?: boolean; xtm?: string; xtr?: string[]; exp?: number } | null {
  const p = decodeFirstSegment(sid)
  // A v2 token's first segment is only its readable HEADER (the payload is sealed, #791):
  // there is no payload to read — callers take those fields from /info instead.
  if (!p || p.v === 2) return null
  return p as never
}

function decodeFirstSegment(sid: string | undefined | null): Record<string, unknown> | null {
  if (!sid) return null
  try {
    const b64 = sid.split('.')[0].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '==='.slice((b64.length + 3) % 4)
    const v = JSON.parse(atob(padded)) as unknown
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/**
 * The readable header of a session token (unverified — display/timing only): expiry, whether
 * the session acts as an account (`bound`), and an opaque identity key. For a v2 token this is
 * all a client can read; for a v1 token it is derived from the readable payload.
 */
export function sessionHeader(sid: string | undefined | null): { v: 1 | 2; exp: number | null; bound: boolean; identityKey: string } | null {
  const p = decodeFirstSegment(sid)
  if (!p) return null
  const exp = typeof p.exp === 'number' ? p.exp : null
  if (p.v === 2) return { v: 2, exp, bound: p.b === 1, identityKey: typeof p.ik === 'string' ? p.ik : '' }
  return {
    v: 1,
    exp,
    bound: typeof p.verifiedEmail === 'string' && !!p.verifiedEmail,
    identityKey: `${typeof p.verifiedEmail === 'string' ? p.verifiedEmail : ''}|${typeof p.at === 'string' ? p.at : ''}`,
  }
}

/**
 * The compound id that re-opens a SOURCE file of an `xt` session: `s_<xtm>_<file>`, or through
 * every pointer of a NESTED route (`xtr` = `[xtm, …inner pointers]`) — so a re-mint lands on
 * the same route the server validated.
 */
export function routeCompound(p: { xtm?: string; xtr?: string[] }, fileId: string): string {
  const route = Array.isArray(p.xtr) && p.xtr.length > 1 ? p.xtr : p.xtm ? [p.xtm] : []
  return route.reduceRight((acc, m) => `s_${m}_${acc}`, fileId)
}

/**
 * The identity-relevant slice of a session token: the verified recipient email +
 * the active tenant (`at`). A per-file sub-session mint (file switch) keeps BOTH
 * stable while changing fileId/rootId; an identity switch (bind-account → a new
 * tenant, or a different verified recipient) changes one of them. Surfaces the
 * "did the CALLER change, not just the opened file" signal so views keyed on it
 * refresh identity-dependent data (e.g. per-file permissions) only when needed.
 */
export function sessionIdentityKey(sid: string | undefined | null): string {
  return sessionHeader(sid)?.identityKey ?? '|'
}
